import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import { barLabel, barShortLabel } from './calendarBarText';
import type { BookingSpan, ChannelMeta } from './calendarHelpers';
import type { PillLayout } from './pillLayout';

interface Props {
  span: BookingSpan;
  channel: ChannelMeta;
  layout: PillLayout;
  laned: boolean;
  compact: boolean;
  labels: 'full' | 'short' | 'none';
  canSeeGuests: boolean;
  operations: boolean;
}

// Bahnen in einer Zeile: oben bündig, die weiteren darunter (Board „Belegung“, Zeile 04).
const LANE_TOP = {
  2: { wide: ['mt-1.5', 'mt-[30px]'], compact: ['mt-[5px]', 'mt-[27px]'] },
  3: { wide: ['mt-1', 'mt-[21px]', 'mt-[38px]'], compact: ['mt-[3px]', 'mt-[18px]', 'mt-[33px]'] },
} as const;

const LANE_HEIGHT = {
  2: { wide: 'h-[22px]', compact: 'h-5' },
  3: { wide: 'h-[15px]', compact: 'h-[13px]' },
} as const;

/** Platz des Balkens in der Zeile: mittig oder in seiner Bahn, abgeblendet, beim Ziehen obenauf. */
export function bookingSlotClasses(opts: {
  lanes: 1 | 2 | 3;
  lane: 0 | 1 | 2;
  compact: boolean;
  dimmed: boolean;
  dragging: boolean;
}) {
  return cn(
    'relative z-10 mx-0.5 flex min-w-0',
    opts.lanes > 1
      ? cn(
          'self-start',
          LANE_TOP[opts.lanes as 2 | 3][opts.compact ? 'compact' : 'wide'][opts.lane],
        )
      : 'self-center',
    opts.dimmed && 'opacity-25',
    opts.dragging && 'z-40 opacity-70',
  );
}

/** Aussehen des Balkens: Höhe nach Bahn und Gerät, gerade Kante am Fensterrand, Kanalfarbe. */
export function bookingBarClasses(
  span: BookingSpan,
  channel: ChannelMeta,
  { lanes, compact, open }: { lanes: 1 | 2 | 3; compact: boolean; open: boolean },
) {
  return cn(
    'relative flex w-full min-w-0 items-center gap-2 overflow-hidden text-left font-bold shadow-sm',
    compact ? 'rounded-sm text-[11px]' : 'text-xs',
    lanes > 1
      ? cn(LANE_HEIGHT[lanes as 2 | 3][compact ? 'compact' : 'wide'], 'rounded-sm')
      : compact
        ? 'h-8'
        : 'h-[34px] rounded-md',
    span.isClippedAtStart && 'rounded-l-none',
    span.isClippedAtEnd && 'rounded-r-none',
    channel.bgClass,
    channel.slug === 'manual' ? 'text-ink' : channel.textClass,
    open && 'ring-forest ring-offset-card ring-2 ring-offset-2',
  );
}

/** Inhalt des Balkens: Kanalzeichen, wo Platz ist, und der Text je Dichte. */
export function BookingBarFace({
  span,
  channel,
  layout,
  laned,
  compact,
  labels,
  ...rights
}: Props) {
  return (
    <span
      className={cn('flex min-w-0 items-center gap-2', compact ? 'px-1.5' : 'px-3')}
      style={{ paddingLeft: layout.contentPadStart, paddingRight: layout.contentPadEnd }}
    >
      {!laned &&
        !compact &&
        (channel.slug === 'manual' ? (
          <span className="bg-ink/12 grid size-5 shrink-0 place-items-center rounded-sm text-[11px] font-extrabold">
            M
          </span>
        ) : (
          <OtaLogo channel={span.channelCategory} size={20} />
        ))}
      {labels !== 'none' && (
        <span className="min-w-0 truncate">
          {labels === 'short'
            ? barShortLabel(span, rights.canSeeGuests)
            : barLabel(span, { canSeeGuests: rights.canSeeGuests, withPlan: rights.operations })}
        </span>
      )}
    </span>
  );
}
